import {
  Card,
  CardHeader,
  Divider,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { SubscriptionTier } from '@worklogix/shared';
import { useEffect, useState } from 'react';

import { ApiError, usePlatformApi } from '../api';
import { EntitlementList } from '../components/EntitlementList';
import { TierBadge } from '../components/TierBadge';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalL,
  },
  seats: {
    display: 'flex',
    alignItems: 'baseline',
    gap: tokens.spacingHorizontalS,
  },
  seatValue: {
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  seatCaption: {
    color: tokens.colorNeutralForeground3,
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXL,
  },
});

/** Render a tier's seat cap: the infinity glyph for an unlimited (null) cap. */
function formatMaxUsers(maxUsers: number | null): string {
  return maxUsers === null ? '∞' : String(maxUsers);
}

/**
 * Read-only "Plans" catalog: the three global subscription tiers fetched from
 * `GET /platform/tiers`, each rendered as a Fluent card with its badge, seat
 * cap (∞ when unlimited) and entitlement list. Purely informational — there are
 * no controls to change a plan here (tier changes live on the tenant detail).
 */
export function PlansView(): JSX.Element {
  const styles = useStyles();
  const api = usePlatformApi();

  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const result = await api.getTiers();
        if (!cancelled) {
          setTiers(result);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof ApiError ? err.message : 'Unable to load plans.';
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (loading) {
    return (
      <div className={styles.center}>
        <Spinner label="Loading plans…" />
      </div>
    );
  }

  const sorted = [...tiers].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className={styles.root}>
      <Title3 as="h1">Plans</Title3>

      {error !== null && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {error === null && sorted.length === 0 ? (
        <Text>No subscription tiers are configured.</Text>
      ) : (
        <div className={styles.cards}>
          {sorted.map((tier) => (
            <Card key={tier.key} className={styles.card} appearance="filled-alternative">
              <CardHeader
                header={<Text weight="semibold">{tier.name}</Text>}
                action={<TierBadge tier={tier} />}
              />
              <div className={styles.seats}>
                <span className={styles.seatValue}>{formatMaxUsers(tier.maxUsers)}</span>
                <Text className={styles.seatCaption} size={200}>
                  {tier.maxUsers === 1 ? 'seat' : 'seats'}
                </Text>
              </div>
              <Divider />
              <EntitlementList features={tier.features} />
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
